import React, { useEffect, useState, useCallback, useMemo } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Row, Col, Dropdown, Nav, Button, Modal, Form } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { DndContext, DragOverlay, useDroppable, useDraggable } from "@dnd-kit/core";
import { getToken } from "../services/authorize";
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from "../contexts/LoadingContext";
import { getCurrentSemester } from "../utils/teachingUtils";
import jsPDF from "jspdf";

const DAYS = [0, 1, 2, 3, 4, 5, 6];
const DAY_NAMES = { 0: "อาทิตย์", 1: "จันทร์", 2: "อังคาร", 3: "พุธ", 4: "พฤหัส", 5: "ศุกร์", 6: "เสาร์" };

// ─── Droppable grid cell ──────────────────────────────────────────────────────
function DroppableCell({ id, isOccupied, children }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  const bg = isOver
    ? isOccupied ? "#ffcccc" : "#d4edda"
    : isOccupied ? "#fff" : "#fafafa";
  return (
    <div
      ref={setNodeRef}
      style={{
        minHeight: 64,
        background: bg,
        border: "1px solid #dee2e6",
        borderRadius: 4,
        padding: 2,
        transition: "background 0.12s",
      }}
    >
      {children}
    </div>
  );
}

// ─── Draggable subject card (sidebar) ─────────────────────────────────────────
function SidebarCard({ subject, assigned }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `sidebar-${subject.sub_id}`,
    data: { type: "sidebar", subject, assigned },
  });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{
        transform: transform ? `translate3d(${transform.x}px,${transform.y}px,0)` : undefined,
        opacity: isDragging ? 0.35 : 1,
        cursor: "grab",
        userSelect: "none",
      }}
      className={`border rounded p-2 mb-1 ${assigned ? "border-success bg-success-subtle" : "border-secondary bg-light"}`}
    >
      <div className="fw-semibold" style={{ fontSize: "0.78rem" }}>{subject.sub_subid}</div>
      <div className="text-muted" style={{ fontSize: "0.71rem", lineHeight: 1.2 }}>{subject.sub_subname}</div>
      {subject.sub_type === "extra" && (
        <span className="text-warning" style={{ fontSize: "0.65rem" }}>เพิ่มเติม</span>
      )}
      {assigned && (
        <div className="text-success fw-semibold" style={{ fontSize: "0.68rem", marginTop: 2 }}>
          <i className="ri-user-line me-1"></i>{assigned.t_name}
        </div>
      )}
    </div>
  );
}

// ─── Draggable slot card (inside grid cell) ───────────────────────────────────
function SlotCard({ slot, onDelete, isDuplicate }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `slot-${slot.teachtime_id}`,
    data: { type: "slot", slot },
  });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{
        transform: transform ? `translate3d(${transform.x}px,${transform.y}px,0)` : undefined,
        opacity: isDragging ? 0.3 : 1,
        cursor: "grab",
        fontSize: "0.72rem",
        position: "relative",
        userSelect: "none",
      }}
      className={`rounded p-1 text-white ${isDuplicate ? "bg-danger" : "bg-primary"}`}
    >
      <button
        type="button"
        className="btn-close btn-close-white position-absolute"
        style={{ top: 2, right: 3, fontSize: "0.5rem", padding: "0.2rem" }}
        title="ลบออกจากตาราง"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onDelete(slot.teachtime_id); }}
      />
      <div className="fw-semibold pe-3" style={{ lineHeight: 1.2 }}>{slot.sub_subid}</div>
      <div style={{ lineHeight: 1.2, opacity: 0.85 }}>{slot.sub_subname}</div>
      <div style={{ lineHeight: 1.2, opacity: 0.7 }}>{slot.t_name}</div>
    </div>
  );
}

// ─── Guidance period cards (คาบแนะแนว — ไม่ผูกกับรายวิชา ครูประจำชั้นเช็คชื่อ) ──
function GuidanceSidebarCard({ placed }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: "sidebar-guidance",
    data: { type: "guidance-sidebar" },
    disabled: !!placed,
  });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{
        transform: transform ? `translate3d(${transform.x}px,${transform.y}px,0)` : undefined,
        opacity: isDragging ? 0.35 : 1,
        cursor: placed ? "default" : "grab",
        userSelect: "none",
      }}
      className={`border rounded p-2 mb-1 ${placed ? "border-warning bg-warning-subtle" : "border-warning bg-light"}`}
    >
      <div className="fw-semibold" style={{ fontSize: "0.78rem" }}>
        <i className="ri-compass-3-line me-1"></i>แนะแนว
      </div>
      <div className="text-muted" style={{ fontSize: "0.71rem", lineHeight: 1.2 }}>กิจกรรมแนะแนว (ครูประจำชั้น)</div>
      {placed && (
        <div className="text-warning-emphasis fw-semibold" style={{ fontSize: "0.68rem", marginTop: 2 }}>
          <i className="ri-checkbox-circle-line me-1"></i>
          วัน{DAY_NAMES[placed.teach_day]} คาบ {placed.period_order}
        </div>
      )}
    </div>
  );
}

function GuidanceSlotCard({ guidance, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `slot-guidance-${guidance.guid_id}`,
    data: { type: "guidance-slot", guidance },
  });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{
        transform: transform ? `translate3d(${transform.x}px,${transform.y}px,0)` : undefined,
        opacity: isDragging ? 0.3 : 1,
        cursor: "grab",
        fontSize: "0.72rem",
        position: "relative",
        userSelect: "none",
      }}
      className="rounded p-1 bg-warning text-dark"
    >
      <button
        type="button"
        className="btn-close position-absolute"
        style={{ top: 2, right: 3, fontSize: "0.5rem", padding: "0.2rem" }}
        title="ลบออกจากตาราง"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onDelete(guidance.guid_id); }}
      />
      <div className="fw-semibold pe-3" style={{ lineHeight: 1.2 }}>แนะแนว</div>
      <div style={{ lineHeight: 1.2, opacity: 0.85 }}>กิจกรรมแนะแนว</div>
      {guidance.advisor_names && (
        <div style={{ lineHeight: 1.2, opacity: 0.7 }}>{guidance.advisor_names}</div>
      )}
    </div>
  );
}

// ─── DragOverlay ghost card ────────────────────────────────────────────────────
function GhostCard({ data }) {
  if (!data) return null;
  if (data.type === "sidebar") {
    const { subject, assigned } = data;
    return (
      <div className="border rounded p-2 bg-white shadow" style={{ width: 140, fontSize: "0.78rem", opacity: 0.95 }}>
        <div className="fw-semibold">{subject.sub_subid}</div>
        <div className="text-muted" style={{ fontSize: "0.71rem" }}>{subject.sub_subname}</div>
        {assigned && <div className="text-success fw-semibold" style={{ fontSize: "0.68rem" }}>{assigned.t_name}</div>}
      </div>
    );
  }
  if (data.type === "slot") {
    const { slot } = data;
    return (
      <div className="rounded p-1 bg-primary text-white shadow" style={{ width: 120, fontSize: "0.72rem", opacity: 0.95 }}>
        <div className="fw-semibold">{slot.sub_subid}</div>
        <div style={{ opacity: 0.85 }}>{slot.sub_subname}</div>
        <div style={{ opacity: 0.7 }}>{slot.t_name}</div>
      </div>
    );
  }
  if (data.type === "guidance-sidebar" || data.type === "guidance-slot") {
    return (
      <div className="rounded p-1 bg-warning text-dark shadow" style={{ width: 120, fontSize: "0.72rem", opacity: 0.95 }}>
        <div className="fw-semibold">แนะแนว</div>
        <div style={{ opacity: 0.85 }}>กิจกรรมแนะแนว</div>
      </div>
    );
  }
  return null;
}

// ─── Main TimetableEditor component ──────────────────────────────────────────
export default function TimetableEditor() {
  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [skin, setSkin] = useState(currentSkin);

  const switchSkin = (s) => {
    const tables = document.getElementsByTagName("table");
    for (const table of tables) {
      const theads = table.querySelectorAll("thead");
      if (s === "dark") {
        for (const thead of theads) { thead.classList.remove("table-secondary"); thead.classList.add("table-primary"); }
        table.classList.add("table-dark");
      } else {
        table.classList.remove("table-dark");
        for (const thead of theads) { thead.classList.remove("table-primary"); thead.classList.add("table-secondary"); }
      }
    }
  };
  switchSkin(skin);
  useEffect(() => { switchSkin(skin); }, [skin]);

  const { c_id } = useParams();
  const cidActive = c_id || "1";
  const { setLoading } = useLoading();

  const [yearId, setYearId] = useState(() => localStorage.getItem("yearId") || null);
  const [selectedYear, setSelectedYear] = useState("");
  const [yearList, setYearList] = useState([]);
  const [classList, setClassList] = useState([]);
  const [roomList, setRoomList] = useState([]);
  const [roomId, setRoomId] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [semester, setSemester] = useState(getCurrentSemester()); // เริ่มที่ภาคเรียนปัจจุบัน
  const [periods, setPeriods] = useState([]);
  const [teachingData, setTeachingData] = useState([]);
  const [guidanceData, setGuidanceData] = useState([]); // คาบแนะแนว (ทั้ง 2 เทอมของห้อง)
  const [subjectList, setSubjectList] = useState([]);
  const [teacherList, setTeacherList] = useState([]);

  // DnD
  const [activeDrag, setActiveDrag] = useState(null);

  // Teacher picker modal
  const [showTeacherModal, setShowTeacherModal] = useState(false);
  const [pendingDrop, setPendingDrop] = useState(null);
  const [selectedTeacherId, setSelectedTeacherId] = useState("");
  const [teacherConflictInfo, setTeacherConflictInfo] = useState(null);

  const authHeader = useCallback(() => ({
    headers: { authorization: `Bearer ${getToken()}` },
  }), []);

  const API = process.env.REACT_APP_API;

  // ─── Derived state ────────────────────────────────────────────────────────
  const grid = useMemo(() => {
    const g = {};
    teachingData.forEach((row) => {
      if (row.teachtime_id === null || row.teach_day === null || !row.period_id) return;
      if (String(row.sub_semester) !== String(semester)) return;
      if (!g[row.teach_day]) g[row.teach_day] = {};
      g[row.teach_day][row.period_id] = {
        teach_id: row.teach_id,
        teachtime_id: row.teachtime_id,
        sub_id: row.sub_id,
        sub_subid: row.sub_subid,
        sub_subname: row.sub_subname,
        sub_semester: row.sub_semester,
        t_id: row.t_id,
        t_name: row.t_name,
        has_teacher_conflict: !!row.has_teacher_conflict,
      };
    });
    return g;
  }, [teachingData, semester]);

  // คาบแนะแนวของเทอมที่เลือก (ห้องละ 1 คาบ/เทอม)
  const guidanceSlot = useMemo(
    () => guidanceData.find((g) => String(g.semester) === String(semester)) || null,
    [guidanceData, semester]
  );

  const isGuidanceCell = useCallback(
    (day, periodId) =>
      !!guidanceSlot && guidanceSlot.teach_day === day && guidanceSlot.period_id === periodId,
    [guidanceSlot]
  );

  const teachingBySubId = useMemo(() => {
    const map = {};
    teachingData.forEach((row) => {
      if (!map[row.sub_id]) {
        map[row.sub_id] = { teach_id: row.teach_id, t_id: row.t_id, t_name: row.t_name };
      }
    });
    return map;
  }, [teachingData]);

  // ─── Data loading ─────────────────────────────────────────────────────────
  useEffect(() => {
    axiosInstance.get(`${API}/getStudyYear`, authHeader()).then((res) => {
      const list = res.data;
      setYearList(list);
      let id = localStorage.getItem("yearId");
      if (!id && list.length > 0) { id = list[0].year_id; localStorage.setItem("yearId", id); }
      setYearId(id);
      const sel = list.find((y) => String(y.year_id) === String(id));
      setSelectedYear(sel ? `ปีการศึกษา ${sel.year_year}` : "");
    }).catch(() => {});
  }, []);

  useEffect(() => {
    axiosInstance.get(`${API}/getClassList`, authHeader()).then((res) => setClassList(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    axiosInstance.get(`${API}/getTeacherList`, authHeader()).then((res) => setTeacherList(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!yearId) return;
    axiosInstance.get(`${API}/getPeriodList/${yearId}`, authHeader()).then((res) => {
      setPeriods(res.data.map((p) => ({
        ...p,
        period_begin: p.period_begin?.slice(0, 5),
        period_end: p.period_end?.slice(0, 5),
      })));
    }).catch(() => setPeriods([]));
  }, [yearId]);

  useEffect(() => {
    if (!cidActive || !yearId) return;
    setRoomId(null);
    setSelectedRoom(null);
    setTeachingData([]);
    setGuidanceData([]);
    setSubjectList([]);
    axiosInstance.get(`${API}/getRoomListByClassId/${cidActive}/${yearId}`, authHeader())
      .then((res) => setRoomList(res.data))
      .catch(() => setRoomList([]));
  }, [cidActive, yearId]);

  const fetchTeachingData = useCallback(() => {
    if (!roomId) return;
    axiosInstance.get(`${API}/getTeachingDataByRoomId/${roomId}`, authHeader())
      .then((res) => setTeachingData(res.data))
      .catch((err) => { if (err.response?.status === 404) setTeachingData([]); });
  }, [roomId]);

  const fetchSubjectList = useCallback(() => {
    if (!roomId || !yearId) return;
    axiosInstance.get(`${API}/getAllSubjectsByRoom/${roomId}/${semester}/${yearId}`, authHeader())
      .then((res) => setSubjectList(res.data))
      .catch(() => setSubjectList([]));
  }, [roomId, semester, yearId]);

  const fetchGuidanceData = useCallback(() => {
    if (!roomId) return;
    axiosInstance.get(`${API}/getGuidanceByRoom/${roomId}`, authHeader())
      .then((res) => setGuidanceData(res.data || []))
      .catch(() => setGuidanceData([]));
  }, [roomId]);

  useEffect(() => { fetchTeachingData(); }, [fetchTeachingData]);
  useEffect(() => { fetchSubjectList(); }, [fetchSubjectList]);
  useEffect(() => { fetchGuidanceData(); }, [fetchGuidanceData]);

  // ─── Room / year selection ─────────────────────────────────────────────────
  const handleSelectRoom = (id) => {
    const room = roomList.find((r) => r.r_id === id);
    if (room) { setRoomId(id); setSelectedRoom(`ม.${room.tb_class_c_id}/${room.r_no} (${room.p_code})`); }
  };

  const handleSelectYear = (id) => {
    const sel = yearList.find((y) => y.year_id === id);
    if (sel) { setYearId(id); setSelectedYear(`ปีการศึกษา ${sel.year_year}`); localStorage.setItem("yearId", id); }
  };

  // ─── Teacher conflict check ───────────────────────────────────────────────
  const checkTeacherConflict = useCallback(async (t_id, day, periodId) => {
    try {
      const { data } = await axiosInstance.get(`${API}/getTeacherSlots/${t_id}/${yearId}`, authHeader());
      return data.find((s) => s.teach_day === day && s.period_id === periodId && s.room_id !== roomId) || null;
    } catch { return null; }
  }, [yearId, roomId]);

  // ─── Core timetable operations ───────────────────────────────────────────
  const confirmIfConflict = async (conflict, action) => {
    if (!conflict) return true;
    const result = await Swal.fire({
      icon: "warning",
      title: "ครูมีชั่วโมงซ้อน",
      html: `ครูมีวิชาสอนห้อง <strong>ม.${conflict.tb_class_c_id}/${conflict.r_no}</strong> ในคาบนี้อยู่แล้ว<br>ต้องการ${action}อยู่ดีหรือไม่?`,
      showCancelButton: true,
      confirmButtonText: action,
      cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#f57c00",
    });
    return result.isConfirmed;
  };

  const doAddTeachtime = async (teach_id, day, periodId, t_id) => {
    const conflict = await checkTeacherConflict(t_id, day, periodId);
    if (!(await confirmIfConflict(conflict, "เพิ่ม"))) return;
    setLoading(true);
    try {
      await axiosInstance.post(`${API}/addTeachtime`, { teach_id, teach_day: day, period_id: periodId }, authHeader());
      fetchTeachingData();
    } catch (err) {
      Swal.fire({ icon: "error", title: "เกิดข้อผิดพลาด", text: err.response?.data?.message || "ไม่สามารถเพิ่มคาบได้" });
    } finally { setLoading(false); }
  };

  const doMoveTeachtime = async (teachtime_id, day, periodId, t_id) => {
    const conflict = await checkTeacherConflict(t_id, day, periodId);
    if (!(await confirmIfConflict(conflict, "ย้าย"))) return;
    setLoading(true);
    try {
      await axiosInstance.put(`${API}/moveTeachtime`, { teachtime_id, teach_day: day, period_id: periodId }, authHeader());
      fetchTeachingData();
    } catch (err) {
      Swal.fire({ icon: "error", title: "เกิดข้อผิดพลาด", text: err.response?.data?.message || "ไม่สามารถย้ายคาบได้" });
    } finally { setLoading(false); }
  };

  const handleDeleteTeachtime = async (teachtime_id) => {
    const { isConfirmed } = await Swal.fire({
      icon: "warning", title: "ยืนยันการลบ", text: "ลบคาบสอนนี้ออกจากตาราง?",
      showCancelButton: true, confirmButtonText: "ลบ", cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#dc3545",
    });
    if (!isConfirmed) return;
    setLoading(true);
    try {
      await axiosInstance.delete(`${API}/deleteTeachtime/${teachtime_id}`, authHeader());
      fetchTeachingData();
      fetchSubjectList();
    } catch (err) {
      Swal.fire({ icon: "error", title: "เกิดข้อผิดพลาด", text: err.response?.data?.message || "ไม่สามารถลบได้" });
    } finally { setLoading(false); }
  };

  // ─── Guidance period operations (คาบแนะแนว) ───────────────────────────────
  const doSetGuidance = async (day, periodId) => {
    setLoading(true);
    try {
      await axiosInstance.post(`${API}/setGuidanceSlot`, {
        r_id: roomId, semester, teach_day: day, period_id: periodId,
      }, authHeader());
      fetchGuidanceData();
    } catch (err) {
      Swal.fire({ icon: "error", title: err.response?.data?.title || "เกิดข้อผิดพลาด", text: err.response?.data?.message || "ไม่สามารถกำหนดคาบแนะแนวได้" });
    } finally { setLoading(false); }
  };

  const doMoveGuidance = async (guid_id, day, periodId) => {
    setLoading(true);
    try {
      await axiosInstance.put(`${API}/moveGuidanceSlot`, {
        guid_id, teach_day: day, period_id: periodId,
      }, authHeader());
      fetchGuidanceData();
    } catch (err) {
      Swal.fire({ icon: "error", title: "เกิดข้อผิดพลาด", text: err.response?.data?.message || "ไม่สามารถย้ายคาบแนะแนวได้" });
    } finally { setLoading(false); }
  };

  const handleDeleteGuidance = async (guid_id) => {
    const { isConfirmed } = await Swal.fire({
      icon: "warning", title: "ยืนยันการลบ", text: "ลบคาบแนะแนวนี้ออกจากตาราง?",
      showCancelButton: true, confirmButtonText: "ลบ", cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#dc3545",
    });
    if (!isConfirmed) return;
    setLoading(true);
    try {
      await axiosInstance.delete(`${API}/deleteGuidanceSlot/${guid_id}`, authHeader());
      fetchGuidanceData();
    } catch (err) {
      Swal.fire({ icon: "error", title: err.response?.data?.title || "เกิดข้อผิดพลาด", text: err.response?.data?.message || "ไม่สามารถลบได้" });
    } finally { setLoading(false); }
  };

  // ─── DnD handlers ────────────────────────────────────────────────────────
  const handleDragStart = ({ active }) => {
    setActiveDrag({ id: active.id, data: active.data.current });
  };

  const handleDragEnd = async ({ active, over }) => {
    setActiveDrag(null);
    if (!over || !String(over.id).startsWith("cell-")) return;
    const parts = String(over.id).split("-");
    const targetDay = parseInt(parts[1]);
    const targetPeriodId = parseInt(parts[2]);
    const activeData = active.data.current;
    const existing = grid[targetDay]?.[targetPeriodId];
    const guidanceHere = isGuidanceCell(targetDay, targetPeriodId);

    if (activeData.type === "guidance-sidebar") {
      if (existing || guidanceHere) {
        Swal.fire({ icon: "error", title: "ไม่สามารถวางได้", text: "ช่องนี้มีวิชาอยู่แล้ว", timer: 2000, showConfirmButton: false });
        return;
      }
      await doSetGuidance(targetDay, targetPeriodId);
      return;
    }
    if (activeData.type === "guidance-slot") {
      const { guidance } = activeData;
      if (guidanceHere) return; // dropped back on its own cell
      if (existing) {
        Swal.fire({ icon: "error", title: "ไม่สามารถวางได้", text: "ช่องนี้มีวิชาอยู่แล้ว", timer: 2000, showConfirmButton: false });
        return;
      }
      await doMoveGuidance(guidance.guid_id, targetDay, targetPeriodId);
      return;
    }
    if (guidanceHere) {
      Swal.fire({ icon: "error", title: "ไม่สามารถวางได้", text: "ช่องนี้เป็นคาบแนะแนว", timer: 2000, showConfirmButton: false });
      return;
    }

    if (activeData.type === "slot") {
      const { slot } = activeData;
      if (existing && existing.teachtime_id === slot.teachtime_id) return;
      if (existing) {
        Swal.fire({ icon: "error", title: "ไม่สามารถวางได้", text: "ช่องนี้มีวิชาอยู่แล้ว", timer: 2000, showConfirmButton: false });
        return;
      }
      await doMoveTeachtime(slot.teachtime_id, targetDay, targetPeriodId, slot.t_id);
    } else if (activeData.type === "sidebar") {
      if (existing) {
        Swal.fire({ icon: "error", title: "ไม่สามารถวางได้", text: "ช่องนี้มีวิชาอยู่แล้ว", timer: 2000, showConfirmButton: false });
        return;
      }
      const { subject, assigned } = activeData;
      if (assigned) {
        await doAddTeachtime(assigned.teach_id, targetDay, targetPeriodId, assigned.t_id);
      } else {
        setPendingDrop({ day: targetDay, periodId: targetPeriodId, subject });
        setSelectedTeacherId("");
        setTeacherConflictInfo(null);
        setShowTeacherModal(true);
      }
    }
  };

  // ─── Teacher picker modal ────────────────────────────────────────────────
  const handleTeacherSelect = async (t_id) => {
    setSelectedTeacherId(t_id);
    if (!t_id || !pendingDrop) { setTeacherConflictInfo(null); return; }
    const conflict = await checkTeacherConflict(parseInt(t_id), pendingDrop.day, pendingDrop.periodId);
    setTeacherConflictInfo(conflict);
  };

  const handleConfirmAssign = async () => {
    if (!selectedTeacherId || !pendingDrop) return;
    const { day, periodId, subject } = pendingDrop;
    if (teacherConflictInfo) {
      const ok = await confirmIfConflict(teacherConflictInfo, "เพิ่ม");
      if (!ok) return;
    }
    setShowTeacherModal(false);
    setLoading(true);
    try {
      await axiosInstance.post(`${API}/assignTeaching`, {
        roomId, subjectId: subject.sub_id, teacherId: parseInt(selectedTeacherId),
        times: [{ teachDay: day, periodId }],
      }, authHeader());
      fetchTeachingData();
      fetchSubjectList();
    } catch (err) {
      Swal.fire({ icon: "error", title: "เกิดข้อผิดพลาด", text: err.response?.data?.message || "ไม่สามารถเพิ่มวิชาได้" });
    } finally {
      setLoading(false);
      setPendingDrop(null);
      setSelectedTeacherId("");
      setTeacherConflictInfo(null);
    }
  };

  const handleCloseTeacherModal = () => {
    setShowTeacherModal(false);
    setPendingDrop(null);
    setSelectedTeacherId("");
    setTeacherConflictInfo(null);
  };

  const pendingPeriodLabel = pendingDrop
    ? `วัน${DAY_NAMES[pendingDrop.day]} คาบ ${periods.find((p) => p.period_id === pendingDrop.periodId)?.period_order ?? ""}`
    : "";

  // ─── Export PDF ───────────────────────────────────────────────────────────
  const handleExportPDF = async () => {
    if (!roomId || periods.length === 0) return;

    const currentRoom = roomList.find((r) => r.r_id === roomId);
    const advisorName = currentRoom?.t_name ? `ครูประจำชั้น: ${currentRoom.t_name}` : "";

    const periodHeaders = periods.map(
      (p) => `<th style="background:#4472c4;color:#fff;padding:6px 4px;font-size:12px;text-align:center;border:1px solid #ccc;">คาบ ${p.period_order}<br/><span style="font-size:10px;font-weight:normal;">${p.period_begin}–${p.period_end}</span></th>`
    ).join("");

    const bodyRows = DAYS.map((d) => {
      const cells = periods.map((p) => {
        const slot = grid[d]?.[p.period_id];
        const guidanceHere = isGuidanceCell(d, p.period_id);
        let content;
        let cellBg = "#fff";
        if (slot) {
          content = `<div style="font-weight:600;font-size:11px;">${slot.sub_subid}</div>
             <div style="font-size:10px;color:#333;">${slot.sub_subname}</div>
             <div style="font-size:10px;color:#555;">${slot.t_name}</div>`;
        } else if (guidanceHere) {
          cellBg = "#fff3cd";
          content = `<div style="font-weight:600;font-size:11px;">แนะแนว</div>
             <div style="font-size:10px;color:#333;">กิจกรรมแนะแนว</div>
             <div style="font-size:10px;color:#555;">${guidanceSlot.advisor_names || "ครูประจำชั้น"}</div>`;
        } else {
          content = `<div style="color:#bbb;font-size:10px;">ว่าง</div>`;
        }
        return `<td style="padding:4px;border:1px solid #ccc;vertical-align:top;background:${cellBg};">${content}</td>`;
      }).join("");
      return `<tr>
        <td style="padding:6px 8px;font-weight:700;font-size:12px;background:#f2f2f2;border:1px solid #ccc;white-space:nowrap;">${DAY_NAMES[d]}</td>
        ${cells}
      </tr>`;
    }).join("");

    const html = `
      <div style="font-family:Tahoma,Arial,sans-serif;padding:20px;background:#fff;color:#111;">
        <h3 style="margin:0 0 4px;font-size:15px;">ตารางสอน — ${selectedRoom || ""}</h3>
        <div style="margin-bottom:2px;font-size:12px;color:#555;">${selectedYear} เทอม ${semester}</div>
        ${advisorName ? `<div style="margin-bottom:10px;font-size:12px;color:#333;">${advisorName}</div>` : '<div style="margin-bottom:10px;"></div>'}
        <table style="border-collapse:collapse;width:100%;">
          <thead>
            <tr>
              <th style="background:#4472c4;color:#fff;padding:6px 8px;font-size:12px;border:1px solid #ccc;white-space:nowrap;">วัน</th>
              ${periodHeaders}
            </tr>
          </thead>
          <tbody>${bodyRows}</tbody>
        </table>
      </div>`;

    const el = document.createElement("div");
    el.style.cssText = "position:fixed;left:-99999px;top:0;background:#fff;";
    el.innerHTML = html;
    document.body.appendChild(el);

    try {
      const { default: html2canvas } = await import("html2canvas");
      const canvas = await html2canvas(el, { scale: 2 });
      const imgData = canvas.toDataURL("image/png");
      const pw = 841.89; // A4 landscape width (pt)
      const ph = 595.28; // A4 landscape height (pt)
      const imgW = pw;
      const imgH = (canvas.height * pw) / canvas.width;
      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      if (imgH <= ph) {
        doc.addImage(imgData, "PNG", 0, 0, imgW, imgH);
      } else {
        // scale down to fit height
        const scale = ph / imgH;
        doc.addImage(imgData, "PNG", 0, 0, imgW * scale, ph);
      }
      const filename = `ตารางสอน_${selectedRoom || "ห้อง"}_เทอม${semester}.pdf`;
      doc.save(filename);
    } finally {
      document.body.removeChild(el);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">

        {/* Top bar */}
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">จัดการการสอน</Link></li>
              <li className="breadcrumb-item active">ตารางสอน</li>
            </ol>
            <h4 className="main-title mb-0">จัดการตารางสอน</h4>
          </div>
          <Dropdown>
            <Dropdown.Toggle variant="primary">{selectedYear || "เลือกปีการศึกษา"}</Dropdown.Toggle>
            <Dropdown.Menu>
              {yearList.map((y) => (
                <Dropdown.Item key={y.year_id} onClick={() => handleSelectYear(y.year_id)}>
                  ปีการศึกษา {y.year_year}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        {/* Class tabs */}
        <Row className="g-12 mb-4">
          <div className="mb-3">
            <Nav variant="tabs" defaultActiveKey={`/teaching/timetable-editor/${cidActive}`}>
              {classList.map((cls, i) => (
                <Nav.Item key={`${cls.c_id}-${i}`}>
                  <Nav.Link
                    as={Link}
                    to={`/teaching/timetable-editor/${cls.c_id}`}
                    active={String(cidActive) === String(cls.c_id)}
                  >
                    {cls.c_class}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
          </div>

          <Card className="card-one">
            <Card.Header className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">ห้องเรียน:</span>
                <Dropdown>
                  <Dropdown.Toggle variant="primary" size="sm">
                    {selectedRoom || "เลือกห้องเรียน"}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {roomList.map((r) => (
                      <Dropdown.Item key={r.r_id} onClick={() => handleSelectRoom(r.r_id)}>
                        ม.{r.tb_class_c_id}/{r.r_no} ({r.p_code})
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
              <div className="d-flex align-items-center gap-2 ms-auto">
                <Nav variant="pills">
                  <Nav.Item>
                    <Nav.Link active={semester === "1"} onClick={() => setSemester("1")} style={{ cursor: "pointer" }}>
                      เทอม 1
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link active={semester === "2"} onClick={() => setSemester("2")} style={{ cursor: "pointer" }}>
                      เทอม 2
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
                <Button
                  variant="outline-danger"
                  size="sm"
                  disabled={!roomId || periods.length === 0}
                  onClick={handleExportPDF}
                >
                  <i className="ri-file-pdf-line me-1"></i>Export PDF
                </Button>
              </div>
            </Card.Header>

            {roomId ? (
              <Card.Body className="p-2">
                <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                  <Row className="g-2">
                    {/* ─── Sidebar ───────────────────────────────────────────── */}
                    <Col xs={12} md={2} style={{ maxHeight: "72vh", overflowY: "auto" }}>
                      <div className="fw-semibold mb-2 text-muted" style={{ fontSize: "0.8rem" }}>
                        วิชาเทอม {semester}
                        <span className="text-secondary fw-normal"> (ลากวางลงตาราง)</span>
                      </div>
                      {subjectList.length === 0
                        ? <div className="text-muted fs-sm">ไม่พบวิชาในหลักสูตร</div>
                        : subjectList.map((s) => (
                            <SidebarCard key={s.sub_id} subject={s} assigned={teachingBySubId[s.sub_id] || null} />
                          ))
                      }
                      <div className="fw-semibold mt-3 mb-2 text-muted" style={{ fontSize: "0.8rem" }}>
                        คาบพิเศษ
                      </div>
                      <GuidanceSidebarCard placed={guidanceSlot} />
                    </Col>

                    {/* ─── Grid ──────────────────────────────────────────────── */}
                    <Col xs={12} md={10} style={{ overflowX: "auto" }}>
                      {periods.length === 0 ? (
                        <div className="text-center text-muted py-5">
                          ยังไม่ได้กำหนดคาบเรียนสำหรับปีนี้
                        </div>
                      ) : (
                        <table
                          className="table table-bordered mb-0"
                          style={{ tableLayout: "fixed", minWidth: periods.length * 90 + 80 }}
                        >
                          <thead className="table-secondary">
                            <tr>
                              <th style={{ width: 80, fontSize: "0.78rem" }}>วัน</th>
                              {periods.map((p) => (
                                <th key={p.period_id} className="text-center" style={{ fontSize: "0.73rem" }}>
                                  <div className="fw-semibold">คาบ {p.period_order}</div>
                                  <div className="text-muted fw-normal">{p.period_begin}–{p.period_end}</div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {DAYS.map((d) => (
                              <tr key={d}>
                                <td className="text-center align-middle fw-semibold" style={{ fontSize: "0.78rem" }}>
                                  {DAY_NAMES[d]}
                                </td>
                                {periods.map((p) => {
                                  const slot = grid[d]?.[p.period_id];
                                  const guidanceHere = isGuidanceCell(d, p.period_id);
                                  return (
                                    <td key={p.period_id} style={{ padding: 2 }}>
                                      <DroppableCell
                                        id={`cell-${d}-${p.period_id}`}
                                        isOccupied={!!slot || guidanceHere}
                                      >
                                        {slot && (
                                          <SlotCard slot={slot} onDelete={handleDeleteTeachtime} isDuplicate={slot.has_teacher_conflict} />
                                        )}
                                        {guidanceHere && (
                                          <GuidanceSlotCard guidance={guidanceSlot} onDelete={handleDeleteGuidance} />
                                        )}
                                      </DroppableCell>
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </Col>
                  </Row>

                  <DragOverlay dropAnimation={null}>
                    {activeDrag && <GhostCard data={activeDrag.data} />}
                  </DragOverlay>
                </DndContext>
              </Card.Body>
            ) : (
              <Card.Body>
                <div className="text-center text-muted py-5">กรุณาเลือกห้องเรียนก่อน</div>
              </Card.Body>
            )}
          </Card>
        </Row>

        <Footer />
      </div>

      {/* ─── Teacher picker modal ────────────────────────────────────────── */}
      <Modal show={showTeacherModal} onHide={handleCloseTeacherModal}>
        <Modal.Header closeButton>
          <Modal.Title>เลือกครูผู้สอน</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingDrop && (
            <div className="mb-3 p-2 bg-light rounded" style={{ fontSize: "0.85rem" }}>
              <div><strong>วิชา:</strong> {pendingDrop.subject.sub_subid} — {pendingDrop.subject.sub_subname}</div>
              <div><strong>ช่วงเวลา:</strong> {pendingPeriodLabel}</div>
            </div>
          )}
          <Form.Select
            value={selectedTeacherId}
            onChange={(e) => handleTeacherSelect(e.target.value)}
          >
            <option value="">-- เลือกครู --</option>
            {teacherList.map((t) => (
              <option key={t.t_id} value={t.t_id}>{t.t_name}</option>
            ))}
          </Form.Select>
          {teacherConflictInfo && (
            <div className="alert alert-warning mt-2 py-1 mb-0" style={{ fontSize: "0.82rem" }}>
              <i className="ri-alert-line me-1"></i>
              ครูมีวิชาสอนห้อง ม.{teacherConflictInfo.tb_class_c_id}/{teacherConflictInfo.r_no} ในคาบนี้อยู่แล้ว
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseTeacherModal}>ยกเลิก</Button>
          <Button variant="success" onClick={handleConfirmAssign} disabled={!selectedTeacherId}>
            ยืนยัน
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
}
